import { useMemo, useState, useEffect } from "react";
import { Badge } from "../../ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { PlusCircle, Save, Loader2 } from "lucide-react";
import { Checkbox } from "../../ui/checkbox";
import { Separator } from "../../ui/separator";
import { X } from "lucide-react";
import { useSearchInterestsQuery } from "../../../services/ad";
import { useDispatch } from "react-redux";
import { setErrorFields } from "../../../store/tableSlice";
import { getIsError } from "../../../utils/getIsError";

export const DropDownCell = ({
  data = {
    values: [],
    selected: null,
    multiple: false,
    allowCreation: false,
    editable: true,
  },
  onUpdate,
  columnInfo,
  isEditable = true,
  row,
  isError,
  isRequired,
  isEmpty,
  errorFields,
}) => {
  // Initialize selected values state
  const [selectedValues, setSelectedValues] = useState([]);
  const [draftSelectedValues, setDraftSelectedValues] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [newOptionText, setNewOptionText] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const dispatch = useDispatch();

  // Add debounce effect for search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch interests if the column is "interest"
  const isInterestColumn = columnInfo?.key === "interest";
  const { data: interestData, isFetching: isLoadingInterests } =
    useSearchInterestsQuery(
      {
        query: debouncedSearchTerm,
        ad_account_id: row?.original?.ad_account_id?.selected?.glid,
      },
      {
        skip:
          !isInterestColumn ||
          !debouncedSearchTerm ||
          !row?.original?.ad_account_id?.selected,
      }
    );

  // Process values list to have consistent format
  const valuesList = useMemo(() => {
    if (isInterestColumn && interestData?.data?.interests) {
      return interestData.data.interests.map((interest) => ({
        value: interest.value,
        display: interest.display,
      }));
    }
    return data.values.map((item) =>
      typeof item === "object" ? item : { value: item, display: item }
    );
  }, [data.values, isInterestColumn, interestData]);

  const filteredValues = useMemo(() => {
    if (isInterestColumn) {
      return valuesList;
    }
    return valuesList.filter((item) =>
      item.display.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [valuesList, searchTerm, isInterestColumn]);

  useEffect(() => {
    if (data.multiple) {
      let selected = Array.isArray(data.selected) ? data.selected : [];
      setSelectedValues(selected.map((item) => item.value));
      setDraftSelectedValues(selected.map((item) => item.value));
      setHasChanges(false);
    } else {
      setSelectedValues(data.selected?.value ?? null);
    }
  }, [data.selected, data.multiple]);

  const handleOptionSelect = (optionValue) => {
    if (data.multiple) {
      // For multiple selection, toggle the value in the draft array
      const currentDraft = [...draftSelectedValues];

      if (currentDraft.includes(optionValue)) {
        // Remove the option if already selected
        const newDraft = currentDraft.filter((val) => val !== optionValue);
        setDraftSelectedValues(newDraft);
        setHasChanges(!arraysEqual(newDraft, selectedValues));
      } else {
        // Add the option if not already selected
        const newDraft = [...currentDraft, optionValue];
        setDraftSelectedValues(newDraft);
        setHasChanges(!arraysEqual(newDraft, selectedValues));
      }
    } else {
      // For single selection, find the full option object
      const selectedOption = data.values.find(
        (opt) => opt.value === optionValue
      );

      // Close dropdown after selection for single select
      setIsOpen(false);

      // Update error state if needed
      if (selectedOption) {
        // Value is valid, remove the field from errorFields
        const updatedErrorFields = errorFields
          .map((error) => {
            if (error.rowId === row.id) {
              const filteredFields = error.fields.filter(
                (field) => field !== columnInfo.key
              );
              return { ...error, fields: filteredFields };
            }
            return error;
          })
          .filter((error) => error.fields.length > 0);

        dispatch(setErrorFields(updatedErrorFields));
      } else if (isRequired) {
        // Value is empty and field is required
        const existingError = errorFields.find(
          (error) => error.rowId === row.id
        );

        if (existingError) {
          const updatedFields = Array.from(
            new Set([...existingError.fields, columnInfo.key])
          );
          dispatch(
            setErrorFields(
              errorFields.map((error) =>
                error.rowId === row.id
                  ? { ...error, fields: updatedFields }
                  : error
              )
            )
          );
        } else {
          dispatch(
            setErrorFields([
              ...errorFields,
              {
                rowId: row.id,
                fields: [columnInfo.key],
              },
            ])
          );
        }
      }

      // Call onUpdate with the new selection
      onUpdate?.({
        ...data,
        selected: selectedOption.value,
      });

      // Update local state
      setSelectedValues(optionValue);
    }
  };

  const handleSaveChanges = () => {
    // For multiple selection, join values with comma
    const selectedString =
      draftSelectedValues.length > 0 ? draftSelectedValues.join(",") : null;

    // Update error state if needed
    if (draftSelectedValues.length > 0) {
      // Values selected, remove from error fields
      dispatch(
        setErrorFields(
          errorFields.filter(
            (error) =>
              error.rowId !== row.id ||
              (error.rowId === row.id && !error.fields.includes(columnInfo.key))
          )
        )
      );
    } else if (isRequired) {
      // No values selected and field is required
      const existingError = errorFields.find((error) => error.rowId === row.id);
      if (existingError) {
        const updatedFields = Array.from(
          new Set([...existingError.fields, columnInfo.key])
        );
        dispatch(
          setErrorFields(
            errorFields.map((error) =>
              error.rowId === row.id
                ? { ...error, fields: updatedFields }
                : error
            )
          )
        );
      } else {
        dispatch(
          setErrorFields([
            ...errorFields,
            {
              rowId: row.id,
              fields: [columnInfo.key],
            },
          ])
        );
      }
    }

    // Call onUpdate with the new selection
    onUpdate?.({
      ...data,
      selected: selectedString,
    });

    setSelectedValues(draftSelectedValues);
    setHasChanges(false);
    setIsOpen(false);
  };

  // Helper function to compare arrays
  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  const handleCreateOption = () => {
    if (!newOptionText.trim() || isCreating) return;

    setIsCreating(true);

    // Create new option
    const newOption = {
      value: newOptionText.trim(),
      display: newOptionText.trim(),
    };

    // Update data with new option
    const updatedValues = [...data.values, newOption];

    // Update selected options
    let updatedSelected;
    if (data.multiple) {
      const newDraft = [...draftSelectedValues, newOption.value];
      setDraftSelectedValues(newDraft);
      setHasChanges(true);
      updatedSelected = newDraft;
    } else {
      updatedSelected = newOption.value;
      onUpdate?.({
        ...data,
        values: updatedValues,
        selected: updatedSelected,
      });
      setSelectedValues(updatedSelected);
    }

    setNewOptionText("");
    setIsCreating(false);
  };

  // Function to get display text for a value
  const getDisplay = (value) => {
    const option = data.values.find((opt) => opt.value === value);
    return option ? option.display : value;
  };

  // Render cell content based on selection state
  const renderContent = () => {
    // If not editable, show simple text representation
    if (!data.editable || !isEditable) {
      if (data.multiple) {
        return Array.isArray(data.selected) && data.selected.length > 0 ? (
          <div className="text-sm">
            {data.selected.map((val, index) => (
              <Badge
                key={`${val.value}-${index}`}
                variant="secondary"
                className="text-xs whitespace-nowrap flex-shrink-0"
              >
                {val.display}
              </Badge>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-400">
            {columnInfo?.display || "Select options..."}
          </div>
        );
      } else {
        // Single selection case
        return (
          <div className="text-sm">
            {data.selected ? (
              data.selected.display
            ) : (
              <span className="text-gray-400">
                {columnInfo?.display || "Select an option..."}
              </span>
            )}
          </div>
        );
      }
    }

    // Show editable content
    return (
      <div className="flex items-center space-x-1 min-w-0 w-full">
        <div className="flex gap-1 items-center overflow-hidden w-full">
          {data.multiple ? (
            // For multiple selection, show badges for each selected item
            Array.isArray(selectedValues) && selectedValues?.length > 0 ? (
              selectedValues.map((val, index) => (
                <Badge
                  key={`${val}-${index}`}
                  variant="secondary"
                  className="text-xs whitespace-nowrap flex-shrink-0"
                >
                  {getDisplay(val)}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-gray-400">
                {columnInfo?.display || "Select options..."}
              </span>
            )
          ) : (
            // Single selection
            <span
              className={`text-sm ${selectedValues ? "" : "text-gray-400"}`}
            >
              {selectedValues
                ? getDisplay(selectedValues)
                : columnInfo?.display || "Select an option..."}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Popover
      open={isEditable && data.editable ? isOpen : false}
      onOpenChange={(open) => isEditable && data.editable && setIsOpen(open)}
    >
      <PopoverTrigger asChild>
        <div
          className={cn(
            "h-[32px] w-full px-2 flex items-center rounded",
            data.editable && isEditable && "cursor-pointer group",
            isHovered && "bg-gray-50",
            isOpen && "bg-gray-100",
            isError && "border border-red-500 bg-red-50"
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {isRequired && isEmpty && !isOpen && (
            <span
              className="text-red-500 mr-1 align-middle"
              title="This is a required field."
            >
              *
            </span>
          )}
          {renderContent()}
        </div>
      </PopoverTrigger>
      {data.editable && isEditable && (
        <PopoverContent className="w-[280px] p-2">
          <div className="space-y-4">
            {/* Available options */}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              <div className="flex items-center space-x-2 sticky top-1 bg-white z-10">
                <Input
                  type="text"
                  placeholder={
                    isInterestColumn ? "Search Facebook interests..." : "Search"
                  }
                  className="m-px"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="absolute right-2 top-2.5 p-0.5 rounded-full hover:bg-gray-100 transition-colors"
                    type="button"
                    aria-label="Clear search"
                    onClick={() => setSearchTerm("")}
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>
              {isLoadingInterests ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : filteredValues.length > 0 ? (
                <>
                  {!data.multiple && selectedValues && (
                    <div className="flex items-center justify-end mb-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-sm text-gray-500 hover:text-gray-700"
                        onClick={() => {
                          // Update error state if needed
                          if (isRequired) {
                            const existingError = errorFields.find(
                              (error) => error.rowId === row.id
                            );
                            if (existingError) {
                              const updatedFields = Array.from(
                                new Set([
                                  ...existingError.fields,
                                  columnInfo.key,
                                ])
                              );
                              dispatch(
                                setErrorFields(
                                  errorFields.map((error) =>
                                    error.rowId === row.id
                                      ? { ...error, fields: updatedFields }
                                      : error
                                  )
                                )
                              );
                            } else {
                              dispatch(
                                setErrorFields([
                                  ...errorFields,
                                  {
                                    rowId: row.id,
                                    fields: [columnInfo.key],
                                  },
                                ])
                              );
                            }
                          }

                          // Call onUpdate with null
                          onUpdate?.({
                            ...data,
                            selected: null,
                          });

                          // Update local state
                          setSelectedValues(null);
                          setIsOpen(false);
                        }}
                      >
                        Clear selection
                      </Button>
                    </div>
                  )}
                  {filteredValues.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center space-x-2 cursor-pointer p-1 hover:bg-gray-100 rounded"
                    >
                      {data.multiple ? (
                        <Checkbox
                          id={option.value}
                          checked={
                            Array.isArray(draftSelectedValues) &&
                            draftSelectedValues?.includes(option.value)
                          }
                          onCheckedChange={() =>
                            handleOptionSelect(option.value)
                          }
                        />
                      ) : (
                        <input
                          type="radio"
                          checked={selectedValues === option.value}
                          onChange={() => handleOptionSelect(option.value)}
                          className="h-4 w-4"
                        />
                      )}
                      <span className="text-sm">{option.display}</span>
                    </label>
                  ))}
                </>
              ) : (
                <div className="text-sm text-gray-500 p-1">
                  {isInterestColumn && !searchTerm
                    ? "Type to search for interests..."
                    : "No options available"}
                </div>
              )}
            </div>

            {/* Save changes button for multiple selection */}
            {data.multiple && hasChanges && (
              <div className="pt-2">
                <Button
                  onClick={handleSaveChanges}
                  className="w-full"
                  size="sm"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save Changes
                </Button>
              </div>
            )}

            {/* Create new option section - Hide for interest column */}
            {data.allowCreation && !isInterestColumn && (
              <>
                <Separator />
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Create option
                  </label>
                  <div className="flex items-center space-x-2">
                    <Input
                      placeholder="Type new option..."
                      value={newOptionText}
                      onChange={(e) => setNewOptionText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateOption();
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={handleCreateOption}
                      disabled={!newOptionText.trim() || isCreating}
                    >
                      {isCreating ? (
                        "Creating..."
                      ) : (
                        <>
                          <PlusCircle className="w-4 h-4 mr-1" />
                          Add
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};
